import React, { useState, useEffect } from "react";
import { useNavigate, useParams, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import ItemRow from "../components/ItemRow";
import DocumentChecklist from "../components/DocumentChecklist";

const API_URL = process.env.REACT_APP_BACKEND_URL + "/api";

// Procurement Rules Configuration
// GeM rules are the SAME for all item classifications (General, Research, Scientific)
const PROCUREMENT_RULES = {
  GEM: {
    GENERAL: [
      { max: 100000, mode: "DIRECT_PURCHASE", committee: 0, label: "Direct Purchase (Direct PO)" },
      { max: 1000000, mode: "OEM_COMPARISON", committee: 3, label: "OEM Comparison (Direct PO)" },
      { max: 2000000, mode: "TENDER", committee: 3, label: "Tender" },
      { max: 2500000, mode: "TENDER", committee: 3, label: "Tender" },
      { max: Infinity, mode: "TENDER", committee: 4, label: "Tender (Extended Committee)" }
    ],
    SCIENTIFIC: [
      { max: 100000, mode: "DIRECT_PURCHASE", committee: 0, label: "Direct Purchase (Direct PO)" },
      { max: 1000000, mode: "OEM_COMPARISON", committee: 3, label: "OEM Comparison (Direct PO)" },
      { max: 2000000, mode: "TENDER", committee: 3, label: "Tender" },
      { max: 2500000, mode: "TENDER", committee: 3, label: "Tender" },
      { max: Infinity, mode: "TENDER", committee: 4, label: "Tender (Extended Committee)" }
    ]
  },
  NON_GEM: {
    GENERAL: [
      { max: 500000, mode: "PAC_LPC", committee: 3, label: "PAC / LPC (Direct PO)" },
      { max: 2500000, mode: "TENDER", committee: 3, label: "Tender" },
      { max: Infinity, mode: "TENDER", committee: 4, label: "Tender (Extended Committee)" }
    ],
    SCIENTIFIC: [
      { max: 200000, mode: "DIRECT_PURCHASE", committee: 0, label: "Direct Purchase (Direct PO)" },
      { max: 2500000, mode: "LPC", committee: 3, label: "Local Purchase Committee (LPC)" },
      { max: Infinity, mode: "TENDER", committee: 4, label: "Tender" }
    ]
  }
};

function PurchaseRequisitionForm() {
  const navigate = useNavigate();
  const { id } = useParams();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { user, getAuthHeader, token } = useAuth();
  // Edit mode is triggered by either `?edit=1` or the dedicated `/edit` suffix path.
  const isEditMode = !!id && (searchParams.get("edit") === "1" || /\/edit\/?$/.test(location.pathname));
  const isViewMode = !!id && !isEditMode;

  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [requisition, setRequisition] = useState(null);
  const [budgetHeads, setBudgetHeads] = useState([]);
  const [committeeMembers, setCommitteeMembers] = useState([]);
  const [selectedBudget, setSelectedBudget] = useState(null);
  const [procurementInfo, setProcurementInfo] = useState(null);

  const emptyItem = { 
    serial_no: 1, 
    description: "", 
    hsn_code: "", 
    gst_percentage: null,
    quantity: 1, 
    unit: "Nos", 
    estimated_cost: 0,
    nature_of_item: "CLASS_I_LOCAL"
  };

  const emptyCommitteeMember = { 
    role: "", 
    name: "", 
    employee_id: "",
    designation: "",
    date: "" 
  };

  const emptyFundingSource = {
    fund_type: "",
    budget_head_id: "",
    budget_head: "",
    project_number: "",
    amount: 0
  };

  const [formData, setFormData] = useState({
    // Procurement Channel - After Indenter Info
    procurement_channel: "", // "GEM" or "NON_GEM"
    
    // Item Classification
    item_classification: "GENERAL", // "GENERAL" or "SCIENTIFIC"
    
    // Funding
    funding_mode: "SINGLE", // "SINGLE" or "MULTI"
    fund_type: "INSTITUTE", // INSTITUTE, PROJECT, DDF, SEED_GRANT
    budget_head_id: "",
    budget_head: "",
    project_number: "",
    project_name: "",
    funding_sources: [], // For multi-funding
    
    // Basic fields
    item_description: "",
    purpose: "TEACHING",
    purpose_detail: "",
    scientific_declaration: false,
    category: "CONSUMABLE",
    estimated_value: "",
    items: [{ ...emptyItem }],
    
    // Indent Fields
    technical_specifications: "",
    eligibility_criteria: "",
    fund_availability: true,
    
    // Equipment Details
    equipment_life: "",
    warranty_details: "",
    amc_required: "",
    
    // Delivery & Installation
    delivery_months: 3,
    installation_ready: false,
    installation_remarks: "",
    
    // Data Center & Inspection
    data_center_required: false,
    inspection_required: false,
    inspection_details: "",
    
    // Tender Details
    purchase_committee_date: "",
    tender_mode: "OPEN_DOMESTIC",
    tender_platform: "GEM",
    limited_tender_suppliers: "",
    special_limited_tender_justification: "",
    special_limited_tender_urgency: "",
    pac_justification: "",
    single_tender_justification: "",
    
    // Documents
    pac_document: null,
    justification_note: "",
    comparative_statement: "",
    
    // Certifications
    items_not_in_store: false,
    specs_are_broad: false,
    
    // Committee Members
    purchase_committee_members: [],
    technical_committee_members: [],
    
    // EMD Requirement Declaration
    emd_applicable: null,
    emd_amount: "",
    emd_form_type: "",
    emd_due_date: "",
    emd_exemption_applicable: null,
    emd_exemption_criteria: "",
    emd_tender_reference: "",
    
    // APS / Performance Security
    aps_applicable: null,
    aps_instrument_type: "",
    aps_tender_number: "",
    aps_po_reference: "",
    aps_vendor_name: "",
    aps_pbg_reference: "",
    aps_amount: "",
    aps_issue_date: "",
    aps_expiry_date: "",
    aps_bank_name: "",
    aps_branch_name: "",
    aps_ifsc_code: "",
    aps_remarks: "",
    
    // Integrity Pact
    integrity_pact_applicable: null,
    integrity_pact_po_number: "",
    integrity_pact_tender_number: "",
    integrity_pact_vendor_name: "",
    integrity_pact_reference: "",
    integrity_pact_signing_date: "",
    integrity_pact_expiry_date: "",
    integrity_pact_remarks: "",
    
    // Datacentre Requirements
    datacentre_fund_available: null,
    datacentre_funding_source: "",
    datacentre_equipment: []
  });

  const [approvalWorkflow, setApprovalWorkflow] = useState([]);

  // Budget lookup state (Institute funds)
  const [divisions, setDivisions] = useState([]);
  const [sections, setSections] = useState([]);
  const [lineItems, setLineItems] = useState([]);
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedLineItem, setSelectedLineItem] = useState("");
  const [budgetSummary, setBudgetSummary] = useState(null);
  const [lineItemBudget, setLineItemBudget] = useState(null);

  // Project lookup state (Project funds)
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);

  // Fund Types master (loaded dynamically from prj_fund_types_master)
  const [fundTypes, setFundTypes] = useState([
    { value: "INSTITUTE", label: "Institute Fund", fund_name: "Institute", category: "core", is_core: true },
    { value: "PROJECT", label: "Project Fund", fund_name: "Project", category: "core", is_core: true },
  ]);

  useEffect(() => {
    fetchBudgetHeads();
    fetchCommitteeMembers();
    fetchAllSections();
    fetchProjects();
    fetchFundTypes();
    if (id) fetchRequisition();
  }, [id]);

  const fetchFundTypes = async () => {
    try {
      const r = await axios.get(API_URL + "/fund-types-master", getAuthHeader());
      if (Array.isArray(r.data) && r.data.length) setFundTypes(r.data);
    } catch {}
  };

  // Fetch all sections (subheads) on mount — no division filter
  const fetchAllSections = async () => {
    try {
      const r = await axios.get(API_URL + "/budget-lookup/all-sections", getAuthHeader());
      setSections(r.data);
    } catch {}
  };

  // Fetch line items when section or category changes — backend filters by
  // category → OH-head mapping (NON_CONSUMABLE→OH-35, CONSUMABLE→OH-31, SERVICES→OH-13)
  useEffect(() => {
    if (selectedSection) {
      const url = API_URL + "/budget-lookup/line-items/" + selectedSection
        + (formData.category ? "?category=" + encodeURIComponent(formData.category) : "");
      axios.get(url, getAuthHeader())
        .then(r => setLineItems(r.data))
        .catch(() => setLineItems([]));
    } else { setLineItems([]); setLineItemBudget(null); }
  }, [selectedSection, formData.category]);

  // Fetch projects
  const fetchProjects = async () => {
    try {
      const r = await axios.get(API_URL + "/project-lookup/projects", getAuthHeader());
      setProjects(r.data);
    } catch {}
  };

  // Fund allocations state (RDF, DDF, Travel, etc.)
  const [fundAllocations, setFundAllocations] = useState([]);
  const [selectedAllocation, setSelectedAllocation] = useState(null);

  // Fetch fund allocations when fund type changes
  useEffect(() => {
    // Build mapping of value -> raw fund_name from master (excluding core types)
    const allocTypes = fundTypes.reduce((acc, ft) => {
      if (!ft.is_core && ft.fund_name) acc[ft.value] = ft.fund_name;
      return acc;
    }, {});
    if (allocTypes[formData.fund_type]) {
      axios.get(API_URL + "/project-lookup/fund-allocations?fund_type=" + encodeURIComponent(allocTypes[formData.fund_type]), getAuthHeader())
        .then(r => setFundAllocations(r.data))
        .catch(() => setFundAllocations([]));
      setSelectedAllocation(null);
    } else {
      setFundAllocations([]);
      setSelectedAllocation(null);
    }
  }, [formData.fund_type, fundTypes]);

  // Calculate procurement mode whenever relevant fields change
  useEffect(() => {
    if (formData.procurement_channel && formData.item_classification && formData.estimated_value) {
      const value = parseFloat(formData.estimated_value) || 0;
      const rules = PROCUREMENT_RULES[formData.procurement_channel]?.[formData.item_classification] || [];
      
      for (const rule of rules) {
        if (value <= rule.max) {
          setProcurementInfo({
            mode: rule.mode,
            label: rule.label,
            committee: rule.committee,
            remarks: rule.remarks,
            value: value
          });
          break;
        }
      }
      
      // Fetch approval workflow from backend (includes fund_type AND funding_mode)
      const fundType = formData.funding_mode === "SINGLE" ? formData.fund_type : "INSTITUTE";
      axios.get(API_URL + "/procurement-info?procurement_channel=" + formData.procurement_channel + "&item_classification=" + formData.item_classification + "&estimated_value=" + value + "&fund_type=" + fundType + "&funding_mode=" + formData.funding_mode, getAuthHeader())
        .then(res => setApprovalWorkflow(res.data.approval_workflow || []))
        .catch(() => setApprovalWorkflow([]));
    } else {
      setProcurementInfo(null);
      setApprovalWorkflow([]);
    }
  }, [formData.procurement_channel, formData.item_classification, formData.estimated_value, formData.fund_type, formData.funding_mode]);

  const fetchBudgetHeads = async () => {
    try {
      const response = await axios.get(API_URL + "/budget-heads", getAuthHeader());
      setBudgetHeads(response.data);
    } catch (error) {
      console.error("Failed to fetch budget heads");
    }
  };

  const fetchCommitteeMembers = async () => {
    try {
      const response = await axios.get(API_URL + "/committee-members", getAuthHeader());
      setCommitteeMembers(response.data);
    } catch (error) {
      console.error("Failed to fetch committee members");
    }
  };

  const fetchRequisition = async () => {
    setLoading(true);
    try {
      const response = await axios.get(API_URL + "/requisitions/" + id, getAuthHeader());
      setRequisition(response.data);
      setFormData(prev => ({
        ...prev,
        ...response.data,
        estimated_value: response.data.estimated_value?.toString() || "",
        items: response.data.items?.length > 0 ? response.data.items : [{ ...emptyItem }],
        purchase_committee_members: response.data.purchase_committee_members || [],
        technical_committee_members: response.data.technical_committee_members || [],
        funding_sources: response.data.funding_sources || [],
        item_classification: response.data.item_classification || "GENERAL",
        funding_mode: response.data.funding_mode || "SINGLE",
        justification_note: response.data.justification_note || ""
      }));
      if (response.data.approval_workflow) {
        setApprovalWorkflow(response.data.approval_workflow);
      }
    } catch (error) {
      toast.error("Failed to fetch requisition");
      navigate("/requisitions");
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Auto-set item classification based on purpose
    if (field === "purpose") {
      setFormData(prev => ({ 
        ...prev, 
        [field]: value,
        item_classification: value === "RESEARCH" ? "SCIENTIFIC" : "GENERAL"
      }));
    }
  };

  const handleBudgetSelect = (budgetId) => {
    const budget = budgetHeads.find(b => b.id === budgetId);
    if (budget) {
      setSelectedBudget(budget);
      setFormData(prev => ({
        ...prev,
        budget_head_id: budget.id,
        budget_head: budget.code,
        project_number: budget.fund_type === "PROJECT" ? budget.code : "",
        project_name: budget.name,
        fund_availability: budget.available_amount > 0
      }));
    }
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...formData.items];
    newItems[index] = { ...newItems[index], [field]: value };
    setFormData(prev => ({ ...prev, items: newItems }));
    if (field === "estimated_cost" || field === "quantity") {
      calculateTotalValue(newItems);
    }
  };

  const calculateTotalValue = (items) => {
    const total = items.reduce((sum, item) => {
      return sum + (parseFloat(item.estimated_cost) || 0) * (parseInt(item.quantity) || 1);
    }, 0);
    setFormData(prev => ({ ...prev, estimated_value: total.toString() }));
  };

  const addItem = () => {
    const newSerialNo = formData.items.length + 1;
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, { ...emptyItem, serial_no: newSerialNo }]
    }));
  };

  const removeItem = (index) => {
    if (formData.items.length > 1) {
      const newItems = formData.items
        .filter((_, i) => i !== index)
        .map((item, i) => ({ ...item, serial_no: i + 1 }));
      setFormData(prev => ({ ...prev, items: newItems }));
      calculateTotalValue(newItems);
    }
  };

  // Multi-funding handlers
  const addFundingSource = () => {
    setFormData(prev => ({
      ...prev,
      funding_sources: [...prev.funding_sources, { ...emptyFundingSource }]
    }));
  };

  const removeFundingSource = (index) => {
    const newSources = formData.funding_sources.filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, funding_sources: newSources }));
  };

  const handleFundingSourceChange = (index, field, value) => {
    setFormData(prev => {
      const newSources = [...prev.funding_sources];
      newSources[index] = { ...newSources[index], [field]: value };
      return { ...prev, funding_sources: newSources };
    });
  };

  // Committee handlers
  const addCommitteeMember = (type) => {
    const key = type === "purchase" ? "purchase_committee_members" : "technical_committee_members";
    setFormData(prev => ({
      ...prev,
      [key]: [...prev[key], { ...emptyCommitteeMember }]
    }));
  };

  const removeCommitteeMember = (type, index) => {
    const key = type === "purchase" ? "purchase_committee_members" : "technical_committee_members";
    const newMembers = formData[key].filter((_, i) => i !== index);
    setFormData(prev => ({ ...prev, [key]: newMembers }));
  };

  const handleCommitteeMemberChange = (type, index, field, value) => {
    const key = type === "purchase" ? "purchase_committee_members" : "technical_committee_members";
    const newMembers = [...formData[key]];
    if (field === "member_id") {
      const member = committeeMembers.find(m => m.id === value);
      if (member) {
        newMembers[index] = {
          ...newMembers[index],
          member_id: value,
          name: member.name,
          employee_id: member.employee_id,
          designation: member.designation
        };
      }
    } else {
      newMembers[index] = { ...newMembers[index], [field]: value };
    }
    setFormData(prev => ({ ...prev, [key]: newMembers }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.procurement_channel) {
      toast.error("Please select Procurement Channel (GeM or Non-GeM)");
      return;
    }
    
    const hasValidItems = formData.items.some(item => item.description.trim() !== "");
    if (!hasValidItems) {
      toast.error("Please add at least one item with description");
      return;
    }
    
    if (!formData.estimated_value || parseFloat(formData.estimated_value) <= 0) {
      toast.error("Total estimated value must be greater than zero");
      return;
    }
    
    const estimatedValue = parseFloat(formData.estimated_value);
    
    if (selectedBudget && estimatedValue > selectedBudget.available_amount) {
      toast.error(`Insufficient funds! Available: ₹${selectedBudget.available_amount.toLocaleString('en-IN')}`);
      return;
    }
    
    // Validate committee requirement
    if (procurementInfo && procurementInfo.committee > 0) {
      const totalCommittee = formData.technical_committee_members.length;
      if (totalCommittee < procurementInfo.committee) {
        toast.error(`This procurement requires minimum ${procurementInfo.committee} committee members`);
        return;
      }
    }
    
    // Multi-funding validation
    if (formData.funding_mode === "MULTI") {
      if (formData.funding_sources.length === 0) {
        toast.error("Please add at least one funding source for multi-funding");
        return;
      }
      const fundingTotal = formData.funding_sources.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
      if (Math.abs(fundingTotal - parseFloat(formData.estimated_value)) >= 1) {
        toast.error(`Funding sources total (₹${fundingTotal.toLocaleString("en-IN")}) must equal estimated value (₹${parseFloat(formData.estimated_value).toLocaleString("en-IN")})`);
        return;
      }
    }

    // Non-GeM justification validation
    if (formData.procurement_channel === "NON_GEM" && !formData.justification_note?.trim()) {
      toast.error("Non-GeM justification note is required");
      return;
    }

    // Purpose detail validation
    if (formData.purpose === "OTHERS" && !formData.purpose_detail?.trim()) {
      toast.error("Please specify the purpose when selecting 'Others'");
      return;
    }
    
    // Document validations
    if (formData.procurement_channel === "NON_GEM" && procurementInfo?.mode === "PAC_LPC" && !formData.pac_justification) {
      toast.error("PAC Justification is required for Non-GeM PAC/LPC purchases");
      return;
    }

    const itemDescriptions = formData.items
      .filter(item => item.description.trim())
      .map(item => item.serial_no + ". " + item.description + " (Qty: " + item.quantity + " " + item.unit + ")")
      .join("\n");

    setSubmitting(true);
    try {
      // Clean up optional numeric fields — backend rejects empty strings for floats.
      const optionalFloatFields = [
        "emd_amount", "aps_amount", "estimated_value",
        "delivery_months", "datacentre_funding_source"
      ];
      const cleaned = { ...formData };
      Object.keys(cleaned).forEach(k => {
        if (cleaned[k] === "" && optionalFloatFields.includes(k)) cleaned[k] = null;
      });
      const payload = {
        ...cleaned,
        item_description: itemDescriptions || formData.item_description,
        estimated_value: parseFloat(formData.estimated_value),
        delivery_months: parseInt(formData.delivery_months) || 3,
        procurement_mode: procurementInfo?.mode || "DIRECT_PURCHASE",
        items: formData.items
          .filter(item => item.description.trim())
          .map(item => ({
            ...item,
            quantity: parseInt(item.quantity) || 1,
            estimated_cost: parseFloat(item.estimated_cost) || 0,
            gst_percentage: (item.gst_percentage === "" || item.gst_percentage == null) ? null : parseFloat(item.gst_percentage)
          }))
      };
      if (isEditMode) {
        await axios.put(API_URL + "/requisitions/" + id, payload, getAuthHeader());
        toast.success("Purchase requisition updated");
      } else {
        await axios.post(API_URL + "/requisitions", payload, getAuthHeader());
        toast.success("Purchase requisition created successfully");
      }
      navigate("/requisitions");
    } catch (error) {
      const detail = error.response?.data?.detail;
      // FastAPI 422 returns detail as an array of validation objects — flatten to readable string
      let msg;
      if (Array.isArray(detail)) {
        msg = detail.map(d => {
          const field = Array.isArray(d.loc) ? d.loc.slice(1).join(".") : (d.loc || "field");
          return `${field}: ${d.msg}`;
        }).join("; ");
      } else if (typeof detail === "string") {
        msg = detail;
      } else {
        msg = "Failed to create requisition";
      }
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (action) => {
    setSubmitting(true);
    try {
      await axios.put(API_URL + "/requisitions/" + id + "/approve", { action, remarks: "" }, getAuthHeader());
      toast.success("Requisition " + action.toLowerCase() + "d successfully");
      fetchRequisition();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process approval");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return React.createElement("div", { className: "flex items-center justify-center min-h-96" },
      React.createElement("p", null, "Loading...")
    );
  }

  const estimatedValue = parseFloat(formData.estimated_value) || 0;
  const filteredBudgetHeads = budgetHeads.filter(b => {
    if (formData.fund_type === "INSTITUTE") return b.fund_type === "INSTITUTE";
    return b.fund_type === "PROJECT";
  });
  const showIndentSection = procurementInfo && procurementInfo.mode !== "DIRECT_PURCHASE";
  const showCommitteeSection = procurementInfo && procurementInfo.committee > 0;
  // Tender sections (Equipment Q&A, Tender Details, Datacentre, Certifications) only for Tender Module
  // PAC_LPC, LPC, OEM_COMPARISON are "direct PO" modes — only show Committee, not Tender sections
  const showTenderSection = procurementInfo && procurementInfo.mode === "TENDER";
  const requiredCommitteeMembers = procurementInfo?.committee || 0;
  
  const committeeRoles = ["Indentor", "Member", "Professor/HoD (Chairperson)"];

  // Header Section
  const headerSection = React.createElement("div", { className: "flex items-center gap-4 mb-6" },
    React.createElement(Button, { variant: "outline", onClick: () => navigate("/requisitions"), "data-testid": "back-btn" }, "← Back"),
    React.createElement("div", null,
      React.createElement("h1", { className: "text-2xl font-bold text-slate-900" },
        isViewMode ? "Requisition " + (requisition?.requisition_number || "") : (isEditMode ? "Edit Requisition " + (requisition?.requisition_number || "") : "New Purchase Indent")
      ),
      React.createElement("p", { className: "text-slate-500" },
        isViewMode ? "Status: " + (requisition?.status || "") : (isEditMode ? "Editing — changes will overwrite the existing draft" : "IIT Hyderabad - Stores & Purchase Section")
      )
    )
  );

  // Approval Actions
  const approvalSection = isViewMode && requisition?.status === "PENDING" && user?.role !== "INDENTER"
    ? React.createElement(Card, { className: "border-amber-200 bg-amber-50 mb-6" },
        React.createElement(CardContent, { className: "p-4 flex gap-2" },
          React.createElement(Button, { onClick: () => handleApproval("APPROVE"), className: "bg-emerald-600", disabled: submitting }, "Approve"),
          React.createElement(Button, { variant: "outline", onClick: () => handleApproval("RETURN"), disabled: submitting }, "Return"),
          React.createElement(Button, { variant: "outline", onClick: () => handleApproval("REJECT"), className: "text-rose-600", disabled: submitting }, "Reject")
        )
      )
    : null;

  // Indenter Info Card
  const indenterCard = React.createElement(Card, { className: "mb-6" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-slate-50" },
      React.createElement(CardTitle, { className: "text-lg" }, "Indenting Department Information")
    ),
    React.createElement(CardContent, { className: "p-6 grid grid-cols-2 md:grid-cols-5 gap-4" },
      React.createElement("div", null,
        React.createElement(Label, { className: "text-slate-500 text-sm" }, "Indenter Name"),
        React.createElement("p", { className: "font-medium" }, user?.name)
      ),
      React.createElement("div", null,
        React.createElement(Label, { className: "text-slate-500 text-sm" }, "Employee ID"),
        React.createElement("p", { className: "font-medium" }, user?.employee_id)
      ),
      React.createElement("div", null,
        React.createElement(Label, { className: "text-slate-500 text-sm" }, "Department"),
        React.createElement("p", { className: "font-medium" }, user?.department)
      ),
      React.createElement("div", null,
        React.createElement(Label, { className: "text-slate-500 text-sm" }, "Designation"),
        React.createElement("p", { className: "font-medium" }, user?.designation)
      ),
      React.createElement("div", null,
        React.createElement(Label, { className: "text-slate-500 text-sm" }, "Date"),
        React.createElement("p", { className: "font-medium" }, new Date().toLocaleDateString('en-IN'))
      )
    )
  );

  // Procurement Channel Card - AFTER Indenter Info
  const procurementChannelCard = React.createElement(Card, { className: "mb-6 border-2 " + (formData.procurement_channel ? "border-slate-200" : "border-amber-400") },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-gradient-to-r from-blue-50 to-indigo-50" },
      React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" }, 
        React.createElement("span", { className: "bg-blue-600 text-white text-xs px-2 py-1 rounded" }, "1"),
        "Procurement Channel *"
      )
    ),
    React.createElement(CardContent, { className: "p-6" },
      React.createElement("div", { className: "grid grid-cols-2 gap-4" },
        // GeM Option
        React.createElement("div", { 
          className: "p-5 border-2 rounded-xl cursor-pointer transition-all " + 
            (formData.procurement_channel === "GEM" 
              ? "border-green-500 bg-green-50 shadow-md" 
              : "border-slate-200 hover:border-green-300 hover:bg-green-50/50"),
          onClick: () => !isViewMode && handleChange("procurement_channel", "GEM"),
          "data-testid": "channel-gem"
        },
          React.createElement("div", { className: "flex items-center gap-3" },
            React.createElement("input", {
              type: "radio",
              name: "procurement_channel",
              checked: formData.procurement_channel === "GEM",
              onChange: () => handleChange("procurement_channel", "GEM"),
              disabled: isViewMode,
              className: "w-5 h-5 text-green-600"
            }),
            React.createElement("div", null,
              React.createElement("p", { className: "font-bold text-lg text-green-700" }, "GeM"),
              React.createElement("p", { className: "text-sm text-slate-600" }, "Government e-Marketplace")
            )
          )
        ),
        // Non-GeM Option
        React.createElement("div", { 
          className: "p-5 border-2 rounded-xl cursor-pointer transition-all " + 
            (formData.procurement_channel === "NON_GEM" 
              ? "border-orange-500 bg-orange-50 shadow-md" 
              : "border-slate-200 hover:border-orange-300 hover:bg-orange-50/50"),
          onClick: () => !isViewMode && handleChange("procurement_channel", "NON_GEM"),
          "data-testid": "channel-non-gem"
        },
          React.createElement("div", { className: "flex items-center gap-3" },
            React.createElement("input", {
              type: "radio",
              name: "procurement_channel",
              checked: formData.procurement_channel === "NON_GEM",
              onChange: () => handleChange("procurement_channel", "NON_GEM"),
              disabled: isViewMode,
              className: "w-5 h-5 text-orange-600"
            }),
            React.createElement("div", null,
              React.createElement("p", { className: "font-bold text-lg text-orange-700" }, "Non-GeM"),
              React.createElement("p", { className: "text-sm text-slate-600" }, "Traditional Procurement")
            )
          )
        )
      ),
      !formData.procurement_channel && React.createElement("p", { className: "text-amber-600 text-sm mt-3 font-medium" },
        "⚠ Please select a procurement channel to continue"
      )
    )
  );

  // Item Classification Card
  const classificationCard = formData.procurement_channel ? React.createElement(Card, { className: "mb-6" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-gradient-to-r from-purple-50 to-pink-50" },
      React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
        React.createElement("span", { className: "bg-purple-600 text-white text-xs px-2 py-1 rounded" }, "2"),
        "Item Classification *"
      )
    ),
    React.createElement(CardContent, { className: "p-6" },
      React.createElement("div", { className: "grid grid-cols-2 gap-4" },
        // General Items
        React.createElement("div", { 
          className: "p-5 border-2 rounded-xl cursor-pointer transition-all " + 
            (formData.item_classification === "GENERAL" 
              ? "border-slate-500 bg-slate-50 shadow-md" 
              : "border-slate-200 hover:border-slate-300"),
          onClick: () => !isViewMode && handleChange("item_classification", "GENERAL")
        },
          React.createElement("div", { className: "flex items-center gap-3" },
            React.createElement("input", {
              type: "radio",
              checked: formData.item_classification === "GENERAL",
              onChange: () => handleChange("item_classification", "GENERAL"),
              disabled: isViewMode,
              className: "w-5 h-5"
            }),
            React.createElement("div", null,
              React.createElement("p", { className: "font-bold" }, "General / Common Use"),
              React.createElement("p", { className: "text-sm text-slate-500" }, "Office supplies, furniture, general equipment")
            )
          )
        ),
        // Scientific Items
        React.createElement("div", { 
          className: "p-5 border-2 rounded-xl cursor-pointer transition-all " + 
            (formData.item_classification === "SCIENTIFIC" 
              ? "border-indigo-500 bg-indigo-50 shadow-md" 
              : "border-slate-200 hover:border-indigo-300"),
          onClick: () => !isViewMode && handleChange("item_classification", "SCIENTIFIC")
        },
          React.createElement("div", { className: "flex items-center gap-3" },
            React.createElement("input", {
              type: "radio",
              checked: formData.item_classification === "SCIENTIFIC",
              onChange: () => handleChange("item_classification", "SCIENTIFIC"),
              disabled: isViewMode,
              className: "w-5 h-5"
            }),
            React.createElement("div", null,
              React.createElement("p", { className: "font-bold text-indigo-700" }, "Research / Scientific"),
              React.createElement("p", { className: "text-sm text-slate-500" }, "Lab equipment, research consumables, project items")
            )
          )
        )
      ),
      formData.item_classification === "SCIENTIFIC" && React.createElement("div", { className: "mt-4 p-3 bg-indigo-50 rounded-lg border border-indigo-200 flex items-start gap-2" },
        React.createElement("input", {
          type: "checkbox",
          checked: formData.scientific_declaration,
          onChange: (e) => handleChange("scientific_declaration", e.target.checked),
          disabled: isViewMode,
          className: "mt-1"
        }),
        React.createElement("label", { className: "text-sm text-indigo-800" },
          "I declare that items are solely for research/scientific purposes and will be used for academic/research activities."
        )
      ),
      // Category and Purpose fields
      React.createElement("div", { className: "mt-4 grid grid-cols-1 md:grid-cols-2 gap-4" },
        // Category dropdown
        React.createElement("div", null,
          React.createElement(Label, { className: "text-sm font-medium mb-1 block" }, "Category *"),
          React.createElement("select", {
            value: formData.category || "CONSUMABLE",
            onChange: (e) => handleChange("category", e.target.value),
            disabled: isViewMode,
            className: "w-full p-2 border rounded-md text-sm bg-white",
            "data-testid": "category-select"
          },
            React.createElement("option", { value: "CONSUMABLE" }, "Consumable"),
            React.createElement("option", { value: "NON_CONSUMABLE" }, "Non-Consumable"),
            React.createElement("option", { value: "SERVICES" }, "Services")
          ),
          // Budget-availability hint based on selected line item, scoped to the
          // OH-head mapped to the chosen category. Empty/zero ⇒ warn.
          (() => {
            const map = { NON_CONSUMABLE: "oh_35", CONSUMABLE: "oh_31", SERVICES: "oh_13" };
            const oh = map[formData.category];
            if (!oh) return null;
            if (!lineItemBudget) {
              return React.createElement("p", { className: "mt-1 text-[11px] text-slate-500", "data-testid": "category-budget-neutral" },
                "Pick the budget section/line item below to see availability under " + oh.toUpperCase().replace("_", "-"));
            }
            const v = lineItemBudget[oh] || (oh === "oh_13" ? lineItemBudget.oh_36 : 0) || 0;
            return v > 0
              ? React.createElement("p", { className: "mt-1 text-[11px] text-emerald-700", "data-testid": "category-budget-ok" },
                  "\u2713 Available under " + oh.toUpperCase().replace("_", "-") + ": \u20B9" + v.toLocaleString("en-IN"))
              : React.createElement("p", { className: "mt-1 text-[11px] text-rose-700 font-medium", "data-testid": "category-budget-warning" },
                  "\u26A0 No budget allocated under " + oh.toUpperCase().replace("_", "-") + " for this line item. Pick a different line item or category.");
          })()
        ),
        // Purpose dropdown
        React.createElement("div", null,
          React.createElement(Label, { className: "text-sm font-medium mb-1 block" }, "Purpose *"),
          React.createElement("select", {
            value: formData.purpose || "TEACHING",
            onChange: (e) => handleChange("purpose", e.target.value),
            disabled: isViewMode,
            className: "w-full p-2 border rounded-md text-sm bg-white",
            "data-testid": "purpose-select"
          },
            React.createElement("option", { value: "TEACHING" }, "Teaching"),
            React.createElement("option", { value: "RESEARCH" }, "Research"),
            React.createElement("option", { value: "OTHERS" }, "Others")
          )
        )
      ),
      // Purpose detail text input when "Others" is selected
      formData.purpose === "OTHERS" && React.createElement("div", { className: "mt-3" },
        React.createElement(Label, { className: "text-sm font-medium mb-1 block" }, "Please specify purpose *"),
        React.createElement(Input, {
          value: formData.purpose_detail || "",
          onChange: (e) => handleChange("purpose_detail", e.target.value),
          disabled: isViewMode,
          placeholder: "Enter purpose details",
          className: "w-full",
          "data-testid": "purpose-detail-input"
        })
      )
    )
  ) : null;

  // Funding Card
  const fundingCard = formData.procurement_channel ? React.createElement(Card, { className: "mb-6" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-gradient-to-r from-emerald-50 to-teal-50" },
      React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
        React.createElement("span", { className: "bg-emerald-600 text-white text-xs px-2 py-1 rounded" }, "3"),
        "Funding Information *"
      )
    ),
    React.createElement(CardContent, { className: "p-6 space-y-6" },
      // Funding Mode
      React.createElement("div", null,
        React.createElement(Label, { className: "font-medium" }, "Funding Mode"),
        React.createElement("div", { className: "flex gap-4 mt-2" },
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              checked: formData.funding_mode === "SINGLE",
              onChange: () => handleChange("funding_mode", "SINGLE"),
              disabled: isViewMode
            }),
            React.createElement("span", null, "Single Source")
          ),
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              checked: formData.funding_mode === "MULTI",
              onChange: () => handleChange("funding_mode", "MULTI"),
              disabled: isViewMode
            }),
            React.createElement("span", null, "Multi Funding")
          )
        )
      ),
      // Fund Type Selection
      formData.funding_mode === "SINGLE" && React.createElement("div", { className: "space-y-4" },
        React.createElement("div", null,
          React.createElement(Label, { className: "font-medium" }, "Fund Type *"),
          React.createElement("select", {
            value: formData.fund_type,
            onChange: (e) => { handleChange("fund_type", e.target.value); setSelectedBudget(null); setSelectedProject(null); setSelectedSection(""); setSelectedLineItem(""); setLineItemBudget(null); setSelectedAllocation(null); handleChange("budget_head_id", ""); },
            className: "w-full mt-1.5 p-2 border rounded-md text-sm",
            disabled: isViewMode,
            "data-testid": "fund-type-select"
          },
            ...fundTypes.map(ft => React.createElement("option", { key: ft.value, value: ft.value }, ft.label))
          ),
          React.createElement("p", { className: "text-[11px] text-slate-400 mt-1" },
            fundTypes.length, " fund types loaded from master \u2014 ",
            fundTypes.filter(f => !f.is_core).length, " active in `prj_fund_types_master`"
          )
        ),
        // INSTITUTE: Subhead -> Line Item -> Budget Info
        formData.fund_type === "INSTITUTE" && React.createElement("div", { className: "p-4 rounded-xl border border-blue-200 bg-blue-50/30 space-y-4" },
          React.createElement("p", { className: "text-xs font-semibold text-blue-800 uppercase tracking-wider" }, "Institute Budget Allocation"),
          React.createElement("div", { className: "grid grid-cols-2 gap-4" },
            React.createElement("div", null,
              React.createElement(Label, { className: "text-xs text-slate-600" }, "Subhead / Section *"),
              React.createElement("select", { value: selectedSection, onChange: (e) => { setSelectedSection(e.target.value); setSelectedLineItem(""); setLineItemBudget(null); }, className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode, "data-testid": "select-section" },
                React.createElement("option", { value: "" }, "-- Select Subhead --"),
                ...sections.map(s => React.createElement("option", { key: s.id, value: s.id }, s.name + " (" + s.code + ")"))
              )
            ),
            React.createElement("div", null,
              React.createElement(Label, { className: "text-xs text-slate-600" }, "Budget Line Item *"),
              React.createElement("select", { value: selectedLineItem, onChange: (e) => {
                setSelectedLineItem(e.target.value);
                const item = lineItems.find(li => li.id === e.target.value);
                if (item) { const allocated = ((item.oh_31||0)+(item.oh_35||0)+(item.oh_36||0))*10000000; setLineItemBudget({ category: item.category, description: item.description, oh_31: (item.oh_31||0)*10000000, oh_35: (item.oh_35||0)*10000000, oh_36: (item.oh_36||0)*10000000, allocated, remaining: allocated }); handleChange("budget_head", item.category+" - "+item.description); handleChange("budget_head_id", item.id); } else { setLineItemBudget(null); }
              }, className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode||!selectedSection, "data-testid": "select-line-item" },
                React.createElement("option", { value: "" }, lineItems.length ? "-- Select Line Item --" : "Select subhead first"),
                ...lineItems.map(li => React.createElement("option", { key: li.id, value: li.id }, li.category+": "+li.description))
              )
            )
          ),
          lineItemBudget && React.createElement("div", { className: "p-3 rounded-lg bg-white border border-blue-100" },
            React.createElement("div", { className: "grid grid-cols-4 gap-4" },
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Budget Allocated"), React.createElement("p", { className: "font-bold text-blue-700 text-lg" }, "\u20B9"+lineItemBudget.allocated.toLocaleString("en-IN"))),
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Remaining Budget"), React.createElement("p", { className: "font-bold text-emerald-700 text-lg" }, "\u20B9"+lineItemBudget.remaining.toLocaleString("en-IN"))),
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "OH-31 (Salary)"), React.createElement("p", { className: "font-semibold text-sm" }, "\u20B9"+lineItemBudget.oh_31.toLocaleString("en-IN"))),
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "OH-35 + OH-36"), React.createElement("p", { className: "font-semibold text-sm" }, "\u20B9"+(lineItemBudget.oh_35+lineItemBudget.oh_36).toLocaleString("en-IN")))
            )
          )
        ),
        // PROJECT / SEED_GRANT: Project selector
        ["PROJECT"].includes(formData.fund_type) && React.createElement("div", { className: "p-4 rounded-xl border border-emerald-200 bg-emerald-50/30 space-y-4" },
          React.createElement("p", { className: "text-xs font-semibold text-emerald-800 uppercase tracking-wider" }, "Project Fund \u2014 Project Selection"),
          React.createElement("div", null,
            React.createElement(Label, { className: "text-xs text-slate-600" }, "Select Project *"),
            React.createElement("select", { value: selectedProject?.project_title || "", onChange: (e) => {
              const p = projects.find(pr => pr.project_title === e.target.value);
              setSelectedProject(p||null);
              if (p) { handleChange("project_number", p.project_number||p.project_title||""); handleChange("budget_head", p.project_title||""); }
            }, className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode, "data-testid": "select-project" },
              React.createElement("option", { value: "" }, "-- Select Project --"),
              ...projects.map(p => React.createElement("option", { key: p.project_title||p.id, value: p.project_title }, (p.project_title||"Untitled")+" \u2014 PI: "+(p.pi_name||"N/A")+" ("+(p.department||"")+")"))
            )
          ),
          selectedProject && React.createElement("div", { className: "p-3 rounded-lg bg-white border border-emerald-100" },
            React.createElement("div", { className: "grid grid-cols-4 gap-4" },
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "PI / Incharge"), React.createElement("p", { className: "font-semibold text-sm" }, selectedProject.pi_name||"N/A")),
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Department"), React.createElement("p", { className: "font-semibold text-sm" }, selectedProject.department||"N/A")),
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Sanctioned Amount"), React.createElement("p", { className: "font-bold text-emerald-700" }, "\u20B9"+(selectedProject.sanctioned_amount||selectedProject.total_budget||0).toLocaleString("en-IN"))),
              React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Remaining"), React.createElement("p", { className: "font-bold "+((selectedProject.remaining_amount||0)>0?"text-emerald-600":"text-rose-600") }, "\u20B9"+(selectedProject.remaining_amount||selectedProject.sanctioned_amount||selectedProject.total_budget||0).toLocaleString("en-IN")))
            )
          )
        ),
        // SEED_GRANT / RDF / Travel / Collaborative Travel / DDF / REMF: Show fund allocation from IITH DB
        // Faculty / Department Funds (anything in master that is not a "core" type)
        (() => {
          const ft = fundTypes.find(t => t.value === formData.fund_type);
          return ft && !ft.is_core;
        })() && React.createElement("div", { className: "p-4 rounded-xl border border-amber-200 bg-amber-50/30 space-y-4" },
          React.createElement("p", { className: "text-xs font-semibold text-amber-800 uppercase tracking-wider" },
            ((fundTypes.find(t => t.value === formData.fund_type) || {}).label || formData.fund_type) + " \u2014 Budget Information"
          ),
          fundAllocations.length > 0 ? React.createElement("div", { className: "space-y-3" },
            React.createElement(Label, { className: "text-xs text-slate-600" }, "Select Allocation *"),
            React.createElement("select", { value: selectedAllocation?.id || "", onChange: (e) => {
              const a = fundAllocations.find(al => al.id === e.target.value);
              setSelectedAllocation(a||null);
              if (a) { handleChange("budget_head", (a.purpose||a.fund_type) + " - " + (a.faculty_name||a.department||"")); handleChange("budget_head_id", a.id); }
            }, className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode, "data-testid": "select-allocation" },
              React.createElement("option", { value: "" }, "-- Select --"),
              ...fundAllocations.map(a => React.createElement("option", { key: a.id, value: a.id },
                (a.sanction_reference||a.fund_type) + " \u2014 " + (a.faculty_name||a.department||"") + " (\u20B9" + (a.allocated_amount||0).toLocaleString("en-IN") + ")"
              ))
            ),
            selectedAllocation && React.createElement("div", { className: "p-3 rounded-lg bg-white border border-amber-100" },
              React.createElement("div", { className: "grid grid-cols-2 md:grid-cols-4 gap-4" },
                React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Allocated"), React.createElement("p", { className: "font-bold text-amber-700 text-lg" }, "\u20B9"+(selectedAllocation.allocated_amount||0).toLocaleString("en-IN"))),
                React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Utilized"), React.createElement("p", { className: "font-semibold text-orange-600" }, "\u20B9"+(selectedAllocation.utilized_amount||0).toLocaleString("en-IN"))),
                React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Available Balance"), React.createElement("p", { className: "font-bold text-emerald-700 text-lg" }, "\u20B9"+(selectedAllocation.balance||(selectedAllocation.allocated_amount||0)-(selectedAllocation.utilized_amount||0)).toLocaleString("en-IN"))),
                React.createElement("div", null,
                  selectedAllocation.faculty_name && React.createElement("div", null, React.createElement("p", { className: "text-xs text-slate-500" }, "Faculty"), React.createElement("p", { className: "font-semibold text-sm" }, selectedAllocation.faculty_name)),
                  selectedAllocation.department && React.createElement("div", { className: "mt-1" }, React.createElement("p", { className: "text-xs text-slate-500" }, "Department"), React.createElement("p", { className: "font-semibold text-sm" }, selectedAllocation.department))
                )
              ),
              selectedAllocation.purpose && React.createElement("p", { className: "text-xs text-slate-500 mt-2 pt-2 border-t border-amber-100" }, "Purpose: ", React.createElement("span", { className: "text-slate-700" }, selectedAllocation.purpose))
            )
          ) : React.createElement("p", { className: "text-sm text-amber-600 italic" }, "No " + formData.fund_type.replace(/_/g, " ") + " allocations found in the database.")
        )
      ),
      // Multi-funding section
      formData.funding_mode === "MULTI" && React.createElement("div", { className: "space-y-4" },
        React.createElement("div", { className: "flex items-center justify-between" },
          React.createElement(Label, { className: "font-medium" }, "Funding Sources"),
          React.createElement(Button, { type: "button", variant: "outline", size: "sm", onClick: addFundingSource, disabled: isViewMode }, "+ Add Source")
        ),
        formData.funding_sources.length === 0
          ? React.createElement("p", { className: "text-slate-500 text-sm italic" }, "No funding sources added. Click '+ Add Source' to add.")
          : formData.funding_sources.map((source, index) => {
              return React.createElement("div", { key: index, className: "p-4 border rounded-xl bg-slate-50/50 space-y-3" },
                React.createElement("div", { className: "flex items-center justify-between" },
                  React.createElement("span", { className: "text-xs font-semibold text-slate-600 uppercase" }, "Source #" + (index + 1)),
                  React.createElement(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => removeFundingSource(index), className: "text-rose-600 h-7", disabled: isViewMode }, "Remove")
                ),
                React.createElement("div", { className: "grid grid-cols-3 gap-3" },
                  React.createElement("div", null,
                    React.createElement(Label, { className: "text-xs text-slate-600" }, "Fund Type"),
                    React.createElement("select", { value: source.fund_type, onChange: (e) => { handleFundingSourceChange(index, "fund_type", e.target.value); handleFundingSourceChange(index, "budget_head", ""); handleFundingSourceChange(index, "section_id", ""); handleFundingSourceChange(index, "line_item_id", ""); }, className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode },
                      React.createElement("option", { value: "" }, "Select"),
                      ...fundTypes.map(ft =>
                        React.createElement("option", { key: ft.value, value: ft.value }, ft.label)
                      )
                    )
                  ),
                  // For INSTITUTE: show Subhead + Line Item
                  source.fund_type === "INSTITUTE" && React.createElement("div", null,
                    React.createElement(Label, { className: "text-xs text-slate-600" }, "Subhead"),
                    React.createElement("select", { value: source.section_id || "", onChange: (e) => { handleFundingSourceChange(index, "section_id", e.target.value); handleFundingSourceChange(index, "line_item_id", ""); }, className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode },
                      React.createElement("option", { value: "" }, "-- Select --"),
                      ...sections.map(s => React.createElement("option", { key: s.id, value: s.id }, s.name))
                    )
                  ),
                  source.fund_type === "INSTITUTE" && React.createElement("div", null,
                    React.createElement(Label, { className: "text-xs text-slate-600" }, "Line Item"),
                    React.createElement("select", { value: source.line_item_id || "", onChange: (e) => { handleFundingSourceChange(index, "line_item_id", e.target.value); const li = lineItems.find(l => l.id === e.target.value); if (li) handleFundingSourceChange(index, "budget_head", li.category+": "+li.description); }, className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode || !source.section_id },
                      React.createElement("option", { value: "" }, "-- Select --"),
                      ...lineItems.filter(li => li.section_id === source.section_id).map(li => React.createElement("option", { key: li.id, value: li.id }, li.category+": "+li.description))
                    )
                  ),
                  // For PROJECT types: show project selector
                  source.fund_type && source.fund_type !== "INSTITUTE" && React.createElement("div", { className: "col-span-2" },
                    React.createElement(Label, { className: "text-xs text-slate-600" }, "Project"),
                    React.createElement("select", { value: source.budget_head || "", onChange: (e) => handleFundingSourceChange(index, "budget_head", e.target.value), className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode },
                      React.createElement("option", { value: "" }, "-- Select Project --"),
                      ...projects
                        .filter(p => source.fund_type === "SEED_GRANT" ? (p.project_type === "Seed Grant" || p.project_type_name === "Seed Grant" || (p.project_title||"").toLowerCase().includes("seed")) : true)
                        .map(p => React.createElement("option", { key: p.project_title||p.id, value: p.project_title }, (p.project_title||"Untitled")+" \u2014 PI: "+(p.pi_name||"N/A")))
                    )
                  )
                ),
                React.createElement("div", { className: "grid grid-cols-3 gap-3" },
                  React.createElement("div", null,
                    React.createElement(Label, { className: "text-xs text-slate-600" }, "Amount (\u20B9) *"),
                    React.createElement(Input, { type: "number", value: source.amount, onChange: (e) => handleFundingSourceChange(index, "amount", e.target.value), className: "mt-1 text-sm", disabled: isViewMode })
                  )
                )
              );
            }),
        // Multi-funding total validation
        formData.funding_sources.length > 0 && React.createElement("div", {
          className: "p-3 rounded-lg border " + (
            Math.abs(formData.funding_sources.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0) - estimatedValue) < 1
              ? "bg-green-50 border-green-200"
              : "bg-red-50 border-red-200"
          )
        },
          React.createElement("div", { className: "flex items-center justify-between text-sm" },
            React.createElement("span", { className: "text-slate-600" }, "Sum of funding sources:"),
            React.createElement("span", { className: "font-bold" },
              "₹" + formData.funding_sources.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0).toLocaleString("en-IN")
            )
          ),
          React.createElement("div", { className: "flex items-center justify-between text-sm mt-1" },
            React.createElement("span", { className: "text-slate-600" }, "Required (Est. Value):"),
            React.createElement("span", { className: "font-bold" }, "₹" + estimatedValue.toLocaleString("en-IN"))
          ),
          Math.abs(formData.funding_sources.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0) - estimatedValue) >= 1 &&
            React.createElement("p", { className: "text-red-600 text-xs mt-2 font-medium" },
              "Total of funding sources must equal the estimated value"
            )
        )
      )
    )
  ) : null;

  // Items Card
  const showNatureOfItem = procurementInfo && (procurementInfo.mode === "TENDER" || procurementInfo.mode === "LPC" || procurementInfo.mode === "OEM_COMPARISON" || procurementInfo.mode === "PAC_LPC");
  const itemsList = formData.items.map((item, index) => 
    React.createElement(ItemRow, {
      key: index,
      item: item,
      index: index,
      isViewMode: isViewMode,
      canRemove: formData.items.length > 1,
      onItemChange: handleItemChange,
      onRemove: () => removeItem(index),
      showNatureOfItem: showNatureOfItem,
      getAuthHeader: getAuthHeader
    })
  );

  // Procurement Mode Info with Approval Workflow
  const WORKFLOW_LABELS = {
    "INDENTER": "Indenter",
    "HOD": "HoD",
    "PI": "PI",
    "COMMITTEE": "Committee",
    "SNP_DA": "S&P DA",
    "SNP_AR": "S&P AR",
    "SNP_HOS": "S&P HoS",
    "INTERNAL_AUDITOR": "Int. Auditor",
    "FNA_DA": "F&A DA",
    "FNA_AR": "F&A AR",
    "FNA_HOS": "F&A HoS",
    "DEAN": "Dean (Admin)",
    "DEAN_SRC": "Dean (SRC)",
    "DEAN_NONGEM": "Dean (Non-GeM)",
    "DEAN_SRC_NONGEM": "Dean SRC (Non-GeM)",
    "DIRECTOR": "Director",
    "SNP_DA_FINAL": "S&P (PO)"
  };

  const procurementModeInfo = procurementInfo
    ? React.createElement("div", { 
        className: "mt-4 p-4 rounded-lg border-2 " + 
        (procurementInfo.mode === "DIRECT_PURCHASE" ? "bg-green-50 border-green-300" :
         procurementInfo.mode === "LPC" || procurementInfo.mode === "OEM_COMPARISON" ? "bg-blue-50 border-blue-300" :
         procurementInfo.mode === "PAC_LPC" ? "bg-orange-50 border-orange-300" :
         "bg-purple-50 border-purple-300"),
        "data-testid": "procurement-mode-info"
      },
        React.createElement("div", { className: "flex items-center justify-between" },
          React.createElement("div", null,
            React.createElement("p", { className: "font-bold text-lg" }, procurementInfo.label),
            procurementInfo.remarks && React.createElement("p", { className: "text-sm text-slate-600 mt-1" }, procurementInfo.remarks)
          ),
          React.createElement("div", { className: "text-right" },
            React.createElement("p", { className: "text-sm text-slate-500" }, "Committee Required"),
            React.createElement("p", { className: "font-bold text-lg" }, 
              procurementInfo.committee === 0 ? "Not Required" : procurementInfo.committee + " Members"
            )
          )
        ),
        // Approval Workflow Preview
        approvalWorkflow.length > 0 && React.createElement("div", { className: "mt-3 pt-3 border-t border-slate-200" },
          React.createElement("p", { className: "text-xs font-semibold text-slate-500 mb-2 uppercase tracking-wider" }, "Approval Chain"),
          React.createElement("div", { className: "flex flex-wrap items-center gap-1" },
            ...approvalWorkflow.map((step, i) => 
              React.createElement(React.Fragment, { key: i },
                React.createElement("span", { 
                  className: "text-xs px-2 py-1 rounded-full font-medium " + 
                    (step === "INDENTER" ? "bg-slate-200 text-slate-700" :
                     step === "HOD" || step === "PI" ? "bg-orange-100 text-orange-800" :
                     step === "COMMITTEE" ? "bg-yellow-100 text-yellow-800" :
                     step.startsWith("SNP") ? "bg-blue-100 text-blue-800" :
                     step.startsWith("FNA") ? "bg-emerald-100 text-emerald-800" :
                     step === "INTERNAL_AUDITOR" ? "bg-amber-100 text-amber-800" :
                     step.startsWith("DEAN") ? "bg-purple-100 text-purple-800" :
                     step === "DIRECTOR" ? "bg-rose-100 text-rose-800" :
                     "bg-slate-100 text-slate-700")
                }, WORKFLOW_LABELS[step] || step),
                i < approvalWorkflow.length - 1 && React.createElement("span", { className: "text-slate-400 text-xs" }, "→")
              )
            )
          )
        ),
        procurementInfo.committee > 0 && React.createElement("p", { className: "text-xs mt-2 text-slate-500" },
          "At least 1 member must be Professor/HoD as Chairperson"
        )
      )
    : null;

  const itemsCard = formData.procurement_channel ? React.createElement(Card, { className: "mb-6" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-slate-50 flex flex-row items-center justify-between" },
      React.createElement("div", null,
        React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
          React.createElement("span", { className: "bg-slate-600 text-white text-xs px-2 py-1 rounded" }, "4"),
          "Item Details"
        ),
        React.createElement("p", { className: "text-sm text-slate-500 mt-1" }, "Item details with Nature of Item per line")
      ),
      !isViewMode && React.createElement(Button, {
        type: "button",
        variant: "outline",
        size: "sm",
        onClick: addItem
      }, "+ Add Item")
    ),
    React.createElement(CardContent, { className: "p-6" },
      React.createElement("div", { className: "space-y-4" }, ...itemsList),
      React.createElement("div", { className: "mt-6 p-4 bg-slate-900 text-white rounded-lg" },
        React.createElement("div", { className: "flex items-center justify-between" },
          React.createElement("span", { className: "text-slate-300" }, "Total Items: " + formData.items.filter(i => i.description.trim()).length),
          React.createElement("div", { className: "text-right" },
            React.createElement("span", { className: "text-slate-300 text-sm" }, "Total Estimated Value"),
            React.createElement("p", { className: "text-2xl font-bold" }, "₹" + estimatedValue.toLocaleString('en-IN'))
          )
        )
      ),
      procurementModeInfo
    )
  ) : null;

  // Equipment Details Card - Q&A format matching Purchase Indent form (Q3-Q7)
  const equipmentCard = showTenderSection ? React.createElement(Card, { className: "mb-6", "data-testid": "equipment-details-card" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-gradient-to-r from-cyan-50 to-sky-50" },
      React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
        React.createElement("span", { className: "bg-cyan-600 text-white text-xs px-2 py-1 rounded" }, "5"),
        "Equipment & Delivery Details"
      )
    ),
    React.createElement(CardContent, { className: "p-6 space-y-5" },
      // Q3: Equipment life, warranty, spares
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("p", { className: "text-sm font-semibold text-slate-700 mb-3" }, "3. Details like the life of the equipment, warranty, availability of spares etc."),
        React.createElement("div", { className: "grid grid-cols-3 gap-4" },
          React.createElement("div", null,
            React.createElement(Label, { className: "text-xs text-slate-500" }, "Expected Equipment Life"),
            React.createElement(Input, {
              value: formData.equipment_life,
              onChange: (e) => handleChange("equipment_life", e.target.value),
              className: "mt-1",
              disabled: isViewMode,
              placeholder: "e.g., 10 years"
            })
          ),
          React.createElement("div", null,
            React.createElement(Label, { className: "text-xs text-slate-500" }, "Warranty Period"),
            React.createElement(Input, {
              value: formData.warranty_details,
              onChange: (e) => handleChange("warranty_details", e.target.value),
              className: "mt-1",
              disabled: isViewMode,
              placeholder: "e.g., 3 years comprehensive"
            })
          ),
          React.createElement("div", null,
            React.createElement(Label, { className: "text-xs text-slate-500" }, "AMC Required?"),
            React.createElement("select", {
              value: formData.amc_required,
              onChange: (e) => handleChange("amc_required", e.target.value),
              className: "w-full mt-1 p-2 border rounded-md",
              disabled: isViewMode
            },
              React.createElement("option", { value: "" }, "Select"),
              React.createElement("option", { value: "YES" }, "Yes"),
              React.createElement("option", { value: "NO" }, "No"),
              React.createElement("option", { value: "OPTIONAL" }, "Optional")
            )
          )
        )
      ),
      // Q4: Delivery period
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("div", { className: "flex items-center gap-3 flex-wrap" },
          React.createElement("p", { className: "text-sm font-semibold text-slate-700" }, "4. Delivery & installation is required within"),
          React.createElement(Input, {
            type: "number",
            value: formData.delivery_months,
            onChange: (e) => handleChange("delivery_months", parseInt(e.target.value) || 3),
            className: "w-20 text-center font-bold",
            min: "1",
            disabled: isViewMode
          }),
          React.createElement("p", { className: "text-sm text-slate-700" }, "months (from the PO issue date)")
        )
      ),
      // Q5: Installation readiness
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("p", { className: "text-sm font-semibold text-slate-700 mb-3" }, "5. Installation requirements like the area, power, civil works etc. are ready:"),
        React.createElement("div", { className: "flex gap-4 ml-4" },
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              name: "installation_ready",
              checked: formData.installation_ready === true,
              onChange: () => handleChange("installation_ready", true),
              disabled: isViewMode
            }),
            React.createElement("span", { className: "text-sm font-medium" }, "YES")
          ),
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              name: "installation_ready",
              checked: formData.installation_ready === false,
              onChange: () => handleChange("installation_ready", false),
              disabled: isViewMode
            }),
            React.createElement("span", { className: "text-sm font-medium" }, "NO")
          )
        ),
        formData.installation_ready === false && React.createElement("div", { className: "mt-3 ml-4" },
          React.createElement(Label, { className: "text-xs text-slate-500" }, "If NO, please provide details of what is required:"),
          React.createElement(Textarea, {
            value: formData.installation_remarks,
            onChange: (e) => handleChange("installation_remarks", e.target.value),
            className: "mt-1",
            disabled: isViewMode,
            placeholder: "Describe installation requirements",
            rows: 2
          })
        )
      ),
      // Q6: Data center
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("p", { className: "text-sm font-semibold text-slate-700 mb-3" }, "6. Any of the items to be procured require space in IITH Data center:"),
        React.createElement("div", { className: "flex gap-4 ml-4" },
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              name: "data_center_required",
              checked: formData.data_center_required === true,
              onChange: () => handleChange("data_center_required", true),
              disabled: isViewMode
            }),
            React.createElement("span", { className: "text-sm font-medium" }, "YES")
          ),
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              name: "data_center_required",
              checked: formData.data_center_required === false,
              onChange: () => handleChange("data_center_required", false),
              disabled: isViewMode
            }),
            React.createElement("span", { className: "text-sm font-medium" }, "NO")
          )
        ),
        formData.data_center_required && React.createElement("p", { className: "mt-2 ml-4 text-xs text-amber-700 bg-amber-50 p-2 rounded" },
          "If yes, please submit the form \u2013 Requisition for installing IT equipment in datacenter"
        )
      ),
      // Q7: Inspection
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("p", { className: "text-sm font-semibold text-slate-700 mb-3" }, "7. Specific inspection schedule needed:"),
        React.createElement("div", { className: "flex gap-4 ml-4" },
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              name: "inspection_required",
              checked: formData.inspection_required === true,
              onChange: () => handleChange("inspection_required", true),
              disabled: isViewMode
            }),
            React.createElement("span", { className: "text-sm font-medium" }, "YES")
          ),
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", {
              type: "radio",
              name: "inspection_required",
              checked: formData.inspection_required === false,
              onChange: () => handleChange("inspection_required", false),
              disabled: isViewMode
            }),
            React.createElement("span", { className: "text-sm font-medium" }, "NO")
          )
        ),
        formData.inspection_required && React.createElement("div", { className: "mt-3 ml-4" },
          React.createElement(Label, { className: "text-xs text-slate-500" }, "If yes, details:"),
          React.createElement(Textarea, {
            value: formData.inspection_details,
            onChange: (e) => handleChange("inspection_details", e.target.value),
            className: "mt-1",
            disabled: isViewMode,
            placeholder: "Provide inspection schedule details",
            rows: 2
          })
        )
      )
    )
  ) : null;

  // Determine if tender mode (for Nature of Item visibility)
  const isTenderMode = procurementInfo && (procurementInfo.mode === "TENDER" || procurementInfo.mode === "LPC" || procurementInfo.mode === "OEM_COMPARISON" || procurementInfo.mode === "PAC_LPC");

  // Tender Details Card - Q&A format matching Purchase Indent (Q8-Q9)
  const tenderDetailsCard = showTenderSection ? React.createElement(Card, { className: "mb-6", "data-testid": "tender-details-card" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-gradient-to-r from-violet-50 to-fuchsia-50" },
      React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
        React.createElement("span", { className: "bg-violet-600 text-white text-xs px-2 py-1 rounded" }, "6"),
        "Mode of Tender & Nature of Item"
      )
    ),
    React.createElement(CardContent, { className: "p-6 space-y-5" },
      // Q8: Nature of item - ONLY for Tender mode
      isTenderMode && React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("p", { className: "text-sm font-semibold text-slate-700 mb-3" }, "8. Nature of item being procured:"),
        React.createElement("div", { className: "space-y-3 ml-4" },
          React.createElement("label", { className: "flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-white " + (formData.nature_of_item === "INDIGENOUS" ? "border-blue-400 bg-blue-50" : "bg-white") },
            React.createElement("input", {
              type: "radio",
              name: "nature_of_item",
              checked: formData.nature_of_item === "INDIGENOUS",
              onChange: () => handleChange("nature_of_item", "INDIGENOUS"),
              disabled: isViewMode,
              className: "mt-1"
            }),
            React.createElement("div", null,
              React.createElement("span", { className: "text-sm font-medium" }, "a) Indigenous Product"),
              React.createElement("p", { className: "text-xs text-slate-500 mt-0.5" }, "Class I Local Supplier \u2013 local content \u2265 50%; Class II \u2013 local content \u2265 20% < 50%")
            )
          ),
          formData.nature_of_item === "INDIGENOUS" && React.createElement("div", { className: "ml-8" },
            React.createElement(Label, { className: "text-xs text-slate-500" }, "Local Content Percentage"),
            React.createElement("select", {
              value: formData.local_content_percentage,
              onChange: (e) => handleChange("local_content_percentage", e.target.value),
              className: "w-full mt-1 p-2 border rounded-md text-sm max-w-xs",
              disabled: isViewMode
            },
              React.createElement("option", { value: "" }, "Select Class"),
              React.createElement("option", { value: "CLASS_I" }, "Class I (Local content \u2265 50%)"),
              React.createElement("option", { value: "CLASS_II" }, "Class II (Local content \u2265 20% and < 50%)")
            )
          ),
          React.createElement("label", { className: "flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-white " + (formData.nature_of_item === "IMPORT" ? "border-orange-400 bg-orange-50" : "bg-white") },
            React.createElement("input", {
              type: "radio",
              name: "nature_of_item",
              checked: formData.nature_of_item === "IMPORT",
              onChange: () => handleChange("nature_of_item", "IMPORT"),
              disabled: isViewMode,
              className: "mt-1"
            }),
            React.createElement("div", null,
              React.createElement("span", { className: "text-sm font-medium" }, "b) Import Product"),
              React.createElement("p", { className: "text-xs text-slate-500 mt-0.5" }, "Non-Local Supplier \u2013 local content < 20%")
            )
          )
        )
      ),
      // Q9: Mode of tender
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("div", { className: "flex items-center gap-3 flex-wrap mb-4" },
          React.createElement("p", { className: "text-sm font-semibold text-slate-700" }, "9. Purchase Committee met on"),
          React.createElement(Input, {
            type: "date",
            value: formData.purchase_committee_date,
            onChange: (e) => handleChange("purchase_committee_date", e.target.value),
            className: "w-44",
            disabled: isViewMode
          }),
          React.createElement("p", { className: "text-sm text-slate-700" }, "and decided the following mode of tender:")
        ),
        React.createElement("div", { className: "space-y-3 ml-4" },
          // a) Open Tender
          React.createElement("label", { className: "flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-white " + (formData.tender_mode === "OPEN_DOMESTIC" || formData.tender_mode === "OPEN_GLOBAL" ? "border-blue-400 bg-blue-50" : "bg-white") },
            React.createElement("input", {
              type: "radio",
              name: "tender_mode",
              checked: formData.tender_mode === "OPEN_DOMESTIC" || formData.tender_mode === "OPEN_GLOBAL",
              onChange: () => handleChange("tender_mode", "OPEN_DOMESTIC"),
              disabled: isViewMode,
              className: "mt-1"
            }),
            React.createElement("div", { className: "flex-1" },
              React.createElement("span", { className: "text-sm font-medium" }, "a) Open Tender"),
              (formData.tender_mode === "OPEN_DOMESTIC" || formData.tender_mode === "OPEN_GLOBAL") && React.createElement("div", { className: "mt-3 grid grid-cols-2 gap-4" },
                React.createElement("div", null,
                  React.createElement(Label, { className: "text-xs text-slate-500" }, "Type of Tender:"),
                  React.createElement("div", { className: "flex gap-4 mt-1" },
                    React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
                      React.createElement("input", {
                        type: "radio",
                        checked: formData.tender_mode === "OPEN_DOMESTIC",
                        onChange: () => handleChange("tender_mode", "OPEN_DOMESTIC"),
                        disabled: isViewMode
                      }),
                      React.createElement("span", { className: "text-sm" }, "Domestic")
                    ),
                    React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
                      React.createElement("input", {
                        type: "radio",
                        checked: formData.tender_mode === "OPEN_GLOBAL",
                        onChange: () => handleChange("tender_mode", "OPEN_GLOBAL"),
                        disabled: isViewMode
                      }),
                      React.createElement("span", { className: "text-sm" }, "Global")
                    )
                  )
                ),
                React.createElement("div", null,
                  React.createElement(Label, { className: "text-xs text-slate-500" }, "Platform of the Tender:"),
                  React.createElement("div", { className: "flex gap-4 mt-1" },
                    React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
                      React.createElement("input", {
                        type: "radio",
                        checked: formData.tender_platform === "GEM",
                        onChange: () => handleChange("tender_platform", "GEM"),
                        disabled: isViewMode || formData.procurement_channel === "NON_GEM"
                      }),
                      React.createElement("span", { className: "text-sm" }, "GeM")
                    ),
                    React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
                      React.createElement("input", {
                        type: "radio",
                        checked: formData.tender_platform === "CPPP",
                        onChange: () => handleChange("tender_platform", "CPPP"),
                        disabled: isViewMode
                      }),
                      React.createElement("span", { className: "text-sm" }, "CPPP")
                    )
                  )
                )
              )
            )
          ),
          // b) Limited Tender
          React.createElement("label", { className: "flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-white " + (formData.tender_mode === "LIMITED" ? "border-blue-400 bg-blue-50" : "bg-white") },
            React.createElement("input", {
              type: "radio",
              name: "tender_mode",
              checked: formData.tender_mode === "LIMITED",
              onChange: () => handleChange("tender_mode", "LIMITED"),
              disabled: isViewMode,
              className: "mt-1"
            }),
            React.createElement("div", { className: "flex-1" },
              React.createElement("span", { className: "text-sm font-medium" }, "b) Limited Tender"),
              React.createElement("div", { className: "text-xs text-slate-500 mt-1 space-y-1" },
                React.createElement("p", null, "i. The number of supplier firms in Limited Tender Enquiry should be \u2265 4."),
                React.createElement("p", null, "ii. Sufficient time should be given for submission of bids")
              ),
              formData.tender_mode === "LIMITED" && React.createElement("div", { className: "mt-3" },
                React.createElement(Label, { className: "text-xs text-slate-600 font-medium" }, "List of suppliers, their addresses and websites *"),
                React.createElement(Textarea, {
                  value: formData.limited_tender_suppliers,
                  onChange: (e) => handleChange("limited_tender_suppliers", e.target.value),
                  className: "mt-1",
                  disabled: isViewMode,
                  placeholder: "1. Firm Name, Address, Website\n2. Firm Name, Address, Website\n3. ...\n4. ...",
                  rows: 4
                })
              )
            )
          ),
          // c) Special Limited Tender
          React.createElement("label", { className: "flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-white " + (formData.tender_mode === "SPECIAL_LIMITED" ? "border-orange-400 bg-orange-50" : "bg-white") },
            React.createElement("input", {
              type: "radio",
              name: "tender_mode",
              checked: formData.tender_mode === "SPECIAL_LIMITED",
              onChange: () => handleChange("tender_mode", "SPECIAL_LIMITED"),
              disabled: isViewMode,
              className: "mt-1"
            }),
            React.createElement("div", { className: "flex-1" },
              React.createElement("span", { className: "text-sm font-medium" }, "c) Special Limited Tender Enquiry"),
              React.createElement("div", { className: "text-xs text-slate-500 mt-1 space-y-1" },
                React.createElement("p", null, "i. Justification for the nature of urgency & reasons why procurement could not be anticipated to be attached."),
                React.createElement("p", null, "ii. Sufficient reasons to be recorded in writing indicating that it will not be in public interest to procure the goods through open tender."),
                React.createElement("p", null, "iii. Sources of supply are definitely known & possibility of fresh source(s) beyond those being tapped is remote.")
              ),
              formData.tender_mode === "SPECIAL_LIMITED" && React.createElement("div", { className: "mt-3 space-y-3" },
                React.createElement("div", null,
                  React.createElement(Label, { className: "text-xs text-orange-700 font-medium" }, "Justification for urgency *"),
                  React.createElement(Textarea, {
                    value: formData.special_limited_tender_justification,
                    onChange: (e) => handleChange("special_limited_tender_justification", e.target.value),
                    className: "mt-1 border-orange-200",
                    disabled: isViewMode,
                    placeholder: "Provide justification for the nature of urgency",
                    rows: 3
                  })
                ),
                React.createElement("div", null,
                  React.createElement(Label, { className: "text-xs text-slate-600 font-medium" }, "List of known suppliers *"),
                  React.createElement(Textarea, {
                    value: formData.limited_tender_suppliers,
                    onChange: (e) => handleChange("limited_tender_suppliers", e.target.value),
                    className: "mt-1",
                    disabled: isViewMode,
                    placeholder: "List known sources of supply",
                    rows: 2
                  })
                )
              )
            )
          ),
          // d) PAC
          React.createElement("label", { className: "flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-white " + (formData.tender_mode === "PAC" ? "border-orange-400 bg-orange-50" : "bg-white") },
            React.createElement("input", {
              type: "radio",
              name: "tender_mode",
              checked: formData.tender_mode === "PAC",
              onChange: () => handleChange("tender_mode", "PAC"),
              disabled: isViewMode,
              className: "mt-1"
            }),
            React.createElement("div", { className: "flex-1" },
              React.createElement("span", { className: "text-sm font-medium" }, "d) Proprietary Article Certificate (PAC)"),
              React.createElement("p", { className: "text-xs text-slate-500 mt-0.5" }, "(please submit prescribed form)"),
              formData.tender_mode === "PAC" && React.createElement("div", { className: "mt-3" },
                React.createElement(Label, { className: "text-xs text-orange-700 font-medium" }, "PAC Justification *"),
                React.createElement(Textarea, {
                  value: formData.pac_justification,
                  onChange: (e) => handleChange("pac_justification", e.target.value),
                  className: "mt-1 border-orange-200",
                  disabled: isViewMode,
                  placeholder: "Provide justification for proprietary article certificate",
                  rows: 3
                })
              )
            )
          ),
          // e) Single Tender
          React.createElement("label", { className: "flex items-start gap-3 p-4 border rounded-lg cursor-pointer hover:bg-white " + (formData.tender_mode === "SINGLE_TENDER" ? "border-orange-400 bg-orange-50" : "bg-white") },
            React.createElement("input", {
              type: "radio",
              name: "tender_mode",
              checked: formData.tender_mode === "SINGLE_TENDER",
              onChange: () => handleChange("tender_mode", "SINGLE_TENDER"),
              disabled: isViewMode,
              className: "mt-1"
            }),
            React.createElement("div", { className: "flex-1" },
              React.createElement("span", { className: "text-sm font-medium" }, "e) Single Tender Enquiry without PAC"),
              React.createElement("p", { className: "text-xs text-slate-500 mt-0.5" }, "(please submit prescribed form)"),
              formData.tender_mode === "SINGLE_TENDER" && React.createElement("div", { className: "mt-3" },
                React.createElement(Label, { className: "text-xs text-orange-700 font-medium" }, "Single Tender Justification *"),
                React.createElement(Textarea, {
                  value: formData.single_tender_justification,
                  onChange: (e) => handleChange("single_tender_justification", e.target.value),
                  className: "mt-1 border-orange-200",
                  disabled: isViewMode,
                  placeholder: "Provide justification for single tender enquiry",
                  rows: 3
                })
              )
            )
          )
        )
      )
    )
  ) : null;

  // Committee Card (Dynamic based on procurement mode)
  const committeeCard = showCommitteeSection && procurementInfo ? React.createElement(Card, { className: "mb-6 border-amber-200" },
    React.createElement(CardHeader, { className: "border-b border-amber-100 bg-amber-50 flex flex-row items-center justify-between" },
      React.createElement("div", null,
        React.createElement(CardTitle, { className: "text-lg text-amber-900" }, "Technical Committee"),
        React.createElement("p", { className: "text-sm text-amber-700" }, 
          "Minimum " + requiredCommitteeMembers + " members required (1 must be Prof/HoD)"
        )
      ),
      !isViewMode && React.createElement(Button, {
        type: "button",
        variant: "outline",
        size: "sm",
        onClick: () => addCommitteeMember("technical")
      }, "+ Add Member")
    ),
    React.createElement(CardContent, { className: "p-6 space-y-4" },
      formData.technical_committee_members.length === 0 
        ? React.createElement("p", { className: "text-slate-500 text-sm italic" }, "Click '+ Add Member' to add committee members.")
        : formData.technical_committee_members.map((member, index) =>
            React.createElement("div", { key: index, className: "p-4 border rounded-lg bg-slate-50 grid grid-cols-5 gap-4 items-end" },
              React.createElement("div", null,
                React.createElement(Label, { className: "text-sm" }, "Role *"),
                React.createElement("select", {
                  value: member.role,
                  onChange: (e) => handleCommitteeMemberChange("technical", index, "role", e.target.value),
                  className: "w-full mt-1 p-2 border rounded-md text-sm",
                  disabled: isViewMode
                },
                  React.createElement("option", { value: "" }, "Select"),
                  ...committeeRoles.map(r => React.createElement("option", { key: r, value: r }, r))
                )
              ),
              React.createElement("div", null,
                React.createElement(Label, { className: "text-sm" }, "Member *"),
                React.createElement("select", {
                  value: member.member_id || "",
                  onChange: (e) => handleCommitteeMemberChange("technical", index, "member_id", e.target.value),
                  className: "w-full mt-1 p-2 border rounded-md text-sm",
                  disabled: isViewMode
                },
                  React.createElement("option", { value: "" }, "Select"),
                  ...committeeMembers.filter(m => m.committee_roles.includes("TECHNICAL_COMMITTEE")).map(m => 
                    React.createElement("option", { key: m.id, value: m.id }, m.name)
                  )
                )
              ),
              React.createElement("div", null,
                React.createElement(Label, { className: "text-sm" }, "Designation"),
                React.createElement(Input, { value: member.designation || "", className: "mt-1 text-sm", disabled: true })
              ),
              React.createElement("div", null,
                React.createElement(Label, { className: "text-sm" }, "Date"),
                React.createElement(Input, {
                  type: "date",
                  value: member.date || "",
                  onChange: (e) => handleCommitteeMemberChange("technical", index, "date", e.target.value),
                  className: "mt-1 text-sm",
                  disabled: isViewMode
                })
              ),
              !isViewMode && React.createElement(Button, {
                type: "button",
                variant: "ghost",
                size: "sm",
                onClick: () => removeCommitteeMember("technical", index),
                className: "text-rose-600"
              }, "Remove")
            )
          ),
      formData.technical_committee_members.length < requiredCommitteeMembers && React.createElement("p", { className: "text-amber-600 text-sm font-medium" },
        "⚠ Add " + (requiredCommitteeMembers - formData.technical_committee_members.length) + " more member(s)"
      )
    )
  ) : null;

  // ===================== EMD REQUIREMENT DECLARATION (Section 7) =====================
  const isTenderSection = showTenderSection;

  // EMD, APS, Integrity Pact are now added post-creation from the Purchase Requests list

  // ===================== DATACENTRE REQUIREMENTS (Section 10) =====================
  // Show when any item has installation_location = "Datacentre"
  const hasDatacentreItem = formData.items.some(it => it.installation_location === "Datacentre");
  
  const addDatacentreRow = () => {
    const newRow = { serial_no: formData.datacentre_equipment.length + 1, specs: "", quantity: 1, rack_unit: "", equipment_type: "", power_required: "", dc_policy_compliant: null };
    handleChange("datacentre_equipment", [...formData.datacentre_equipment, newRow]);
  };
  const updateDcRow = (idx, field, value) => {
    const updated = [...formData.datacentre_equipment];
    updated[idx] = { ...updated[idx], [field]: value };
    handleChange("datacentre_equipment", updated);
  };
  const removeDcRow = (idx) => {
    handleChange("datacentre_equipment", formData.datacentre_equipment.filter((_, i) => i !== idx));
  };

  const datacentreCard = hasDatacentreItem ? React.createElement(Card, { className: "mb-6 border-sky-300", "data-testid": "datacentre-card" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-gradient-to-r from-sky-50 to-blue-50" },
      React.createElement(CardTitle, { className: "text-lg flex items-center gap-2" },
        React.createElement("span", { className: "bg-sky-600 text-white text-xs px-2 py-1 rounded" }, "10"),
        "Datacentre Installation Requirements"
      )
    ),
    React.createElement(CardContent, { className: "p-6 space-y-5" },
      // Fund Availability
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("p", { className: "text-sm font-semibold text-slate-700 mb-3" }, "Availability of Funds for Datacentre Installation:"),
        React.createElement("div", { className: "flex gap-4 ml-4 mb-3" },
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", { type: "radio", checked: formData.datacentre_fund_available === true, onChange: () => handleChange("datacentre_fund_available", true), disabled: isViewMode }),
            React.createElement("span", { className: "text-sm font-medium" }, "YES")
          ),
          React.createElement("label", { className: "flex items-center gap-2 cursor-pointer" },
            React.createElement("input", { type: "radio", checked: formData.datacentre_fund_available === false, onChange: () => handleChange("datacentre_fund_available", false), disabled: isViewMode }),
            React.createElement("span", { className: "text-sm font-medium" }, "NO")
          )
        ),
        formData.datacentre_fund_available === true && React.createElement("div", { className: "ml-4" },
          React.createElement(Label, { className: "text-xs text-slate-500" }, "Funding Source *"),
          React.createElement("select", { value: formData.datacentre_funding_source, onChange: (e) => handleChange("datacentre_funding_source", e.target.value), className: "w-full mt-1 p-2 border rounded-md max-w-xs", disabled: isViewMode },
            React.createElement("option", { value: "" }, "Select"), React.createElement("option", { value: "INSTITUTE" }, "Institute"), React.createElement("option", { value: "DEPARTMENT" }, "Department"), React.createElement("option", { value: "PROJECT" }, "Project")
          )
        )
      ),
      // Equipment Grid
      React.createElement("div", { className: "p-4 bg-slate-50 rounded-lg border" },
        React.createElement("div", { className: "flex items-center justify-between mb-3" },
          React.createElement("p", { className: "text-sm font-semibold text-slate-700" }, "Datacentre Equipment Details"),
          React.createElement(Button, { type: "button", variant: "outline", size: "sm", onClick: addDatacentreRow, disabled: isViewMode }, "+ Add Equipment")
        ),
        formData.datacentre_equipment.length === 0
          ? React.createElement("p", { className: "text-sm text-slate-400 italic" }, "No equipment added. Click '+ Add Equipment' to add rows.")
          : React.createElement("div", { className: "space-y-3" },
              ...formData.datacentre_equipment.map((row, idx) =>
                React.createElement("div", { key: idx, className: "p-3 bg-white border rounded-lg space-y-3" },
                  React.createElement("div", { className: "flex items-center justify-between" },
                    React.createElement("span", { className: "text-xs font-semibold text-slate-500" }, "Equipment #" + (idx + 1)),
                    React.createElement(Button, { type: "button", variant: "ghost", size: "sm", onClick: () => removeDcRow(idx), className: "text-rose-500 text-xs", disabled: isViewMode }, "Remove")
                  ),
                  React.createElement("div", null,
                    React.createElement(Label, { className: "text-xs text-slate-500" }, "Detailed Specifications *"),
                    React.createElement(Textarea, { value: row.specs, onChange: (e) => updateDcRow(idx, "specs", e.target.value), className: "mt-1 text-sm", disabled: isViewMode, rows: 2, placeholder: "Technical description of the equipment" })
                  ),
                  React.createElement("div", { className: "grid grid-cols-4 gap-3" },
                    React.createElement("div", null,
                      React.createElement(Label, { className: "text-xs text-slate-500" }, "Quantity *"),
                      React.createElement(Input, { type: "number", value: row.quantity, onChange: (e) => updateDcRow(idx, "quantity", parseInt(e.target.value) || 1), className: "mt-1 text-sm", disabled: isViewMode, min: "1" })
                    ),
                    React.createElement("div", null,
                      React.createElement(Label, { className: "text-xs text-slate-500" }, "Rack Unit Size (RU) *"),
                      React.createElement(Input, { type: "number", value: row.rack_unit, onChange: (e) => updateDcRow(idx, "rack_unit", e.target.value), className: "mt-1 text-sm", disabled: isViewMode, placeholder: "e.g., 4" })
                    ),
                    React.createElement("div", null,
                      React.createElement(Label, { className: "text-xs text-slate-500" }, "IT Equipment Type *"),
                      React.createElement("select", { value: row.equipment_type, onChange: (e) => updateDcRow(idx, "equipment_type", e.target.value), className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode },
                        React.createElement("option", { value: "" }, "Select"),
                        React.createElement("option", { value: "COMPUTE_SERVER" }, "Compute Server"),
                        React.createElement("option", { value: "GPU_SERVER" }, "GPU Server"),
                        React.createElement("option", { value: "HPC_CLUSTER" }, "HPC Cluster"),
                        React.createElement("option", { value: "STORAGE" }, "Storage"),
                        React.createElement("option", { value: "NETWORK_SWITCH" }, "Network Switch"),
                        React.createElement("option", { value: "BLADE_SERVER" }, "Blade Server"),
                        React.createElement("option", { value: "FIREWALL" }, "Firewall"),
                        React.createElement("option", { value: "ROUTER" }, "Router"),
                        React.createElement("option", { value: "OTHER" }, "Other")
                      )
                    ),
                    React.createElement("div", null,
                      React.createElement(Label, { className: "text-xs text-slate-500" }, "Power Required *"),
                      React.createElement("select", { value: row.power_required, onChange: (e) => updateDcRow(idx, "power_required", e.target.value), className: "w-full mt-1 p-2 border rounded-md text-sm", disabled: isViewMode },
                        React.createElement("option", { value: "" }, "Select"),
                        React.createElement("option", { value: "LT_1000" }, "Less than 1000 kW"),
                        React.createElement("option", { value: "1000_2000" }, "1000 \u2013 2000 kW"),
                        React.createElement("option", { value: "GT_2000" }, "Greater than 2000 kW")
                      )
                    )
                  ),
                  React.createElement("div", { className: "flex items-center gap-3" },
                    React.createElement("p", { className: "text-xs text-slate-600" }, "As per DC Policy:"),
                    React.createElement("label", { className: "flex items-center gap-1 cursor-pointer" },
                      React.createElement("input", { type: "radio", checked: row.dc_policy_compliant === true, onChange: () => updateDcRow(idx, "dc_policy_compliant", true), disabled: isViewMode }),
                      React.createElement("span", { className: "text-xs" }, "Yes")
                    ),
                    React.createElement("label", { className: "flex items-center gap-1 cursor-pointer" },
                      React.createElement("input", { type: "radio", checked: row.dc_policy_compliant === false, onChange: () => updateDcRow(idx, "dc_policy_compliant", false), disabled: isViewMode }),
                      React.createElement("span", { className: "text-xs" }, "No")
                    )
                  )
                )
              )
            )
      ),
      React.createElement("p", { className: "text-xs text-sky-700 bg-sky-50 p-2 rounded" },
        "Upload Technical Specification Document, Rack Layout Diagram, and Power Consumption Sheet in the Documents section below"
      )
    )
  ) : null;

  // Document Requirements Card - always visible when procurement channel is selected
  const showDocumentsCard = !!formData.procurement_channel;
  const documentsCard = showDocumentsCard ? React.createElement(Card, { className: "mb-6" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-slate-50" },
      React.createElement(CardTitle, { className: "text-lg" }, "Document Requirements & Justifications")
    ),
    React.createElement(CardContent, { className: "p-6 space-y-4" },
      // Technical Specifications (only for tender/LPC modes)
      showTenderSection && React.createElement("div", null,
        React.createElement(Label, null, "Technical Specifications *"),
        React.createElement(Textarea, {
          value: formData.technical_specifications,
          onChange: (e) => handleChange("technical_specifications", e.target.value),
          className: "mt-1.5 min-h-24",
          disabled: isViewMode,
          placeholder: "Enter detailed technical specifications"
        })
      ),
      // PAC Justification (for Non-GeM PAC purchases)
      (formData.procurement_channel === "NON_GEM" && procurementInfo?.mode === "PAC_LPC") && React.createElement("div", null,
        React.createElement(Label, { className: "text-orange-700 font-medium" }, "PAC Justification * (Required for Non-GeM PAC)"),
        React.createElement(Textarea, {
          value: formData.pac_justification,
          onChange: (e) => handleChange("pac_justification", e.target.value),
          className: "mt-1.5 min-h-20 border-orange-200",
          disabled: isViewMode,
          placeholder: "Provide justification for PAC purchase"
        })
      ),
      // Justification Note (for all Non-GeM)
      formData.procurement_channel === "NON_GEM" && React.createElement("div", null,
        React.createElement(Label, { className: "font-medium text-amber-700" }, "Non-GeM Justification * (Why not through GeM?)"),
        React.createElement(Textarea, {
          value: formData.justification_note,
          onChange: (e) => handleChange("justification_note", e.target.value),
          className: "mt-1.5 border-amber-200",
          disabled: isViewMode,
          placeholder: "Explain why this purchase cannot be made through GeM"
        }),
        React.createElement("p", { className: "text-xs text-amber-600 mt-1" },
          "Required for all Non-GeM purchases as per GFR 2017"
        )
      )
    )
  ) : null;

  // Certifications Card
  const certificationsCard = showTenderSection ? React.createElement(Card, { className: "mb-6" },
    React.createElement(CardHeader, { className: "border-b border-slate-100 bg-slate-50" },
      React.createElement(CardTitle, { className: "text-lg" }, "Certifications")
    ),
    React.createElement(CardContent, { className: "p-6 space-y-3" },
      React.createElement("label", { className: "flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50" },
        React.createElement("input", {
          type: "checkbox",
          checked: formData.items_not_in_store,
          onChange: (e) => handleChange("items_not_in_store", e.target.checked),
          disabled: isViewMode,
          className: "mt-1"
        }),
        React.createElement("span", { className: "text-sm" }, "Certified that items are not available in department stores")
      ),
      React.createElement("label", { className: "flex items-start gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50" },
        React.createElement("input", {
          type: "checkbox",
          checked: formData.specs_are_broad,
          onChange: (e) => handleChange("specs_are_broad", e.target.checked),
          disabled: isViewMode,
          className: "mt-1"
        }),
        React.createElement("span", { className: "text-sm" }, "Certified that specifications are broad and non-restrictive")
      )
    )
  ) : null;

  // Live validation — compute issues that prevent submission so the user
  // sees ALL blockers at once instead of getting brief toasts.
  const validationIssues = (() => {
    if (isViewMode) return [];
    const issues = [];
    if (!formData.procurement_channel) issues.push("Select Procurement Channel (GeM or Non-GeM) at the top.");
    const validItems = formData.items.filter(i => (i.description || "").trim() !== "");
    if (validItems.length === 0) issues.push("Add at least one Item with a description in the Item Details section.");
    if (!formData.estimated_value || parseFloat(formData.estimated_value) <= 0)
      issues.push("Total Estimated Value must be greater than zero (fill Quantity \u00D7 Unit Cost on at least one item).");
    if (selectedBudget && estimatedValue > selectedBudget.available_amount)
      issues.push("Insufficient funds in selected budget head (Available: \u20B9" + (selectedBudget.available_amount || 0).toLocaleString("en-IN") + ").");
    if (procurementInfo && procurementInfo.committee > 0) {
      const total = formData.technical_committee_members.length;
      if (total < procurementInfo.committee)
        issues.push("Add " + (procurementInfo.committee - total) + " more Technical Committee member(s) (minimum " + procurementInfo.committee + " required for this procurement mode).");
    }
    if (formData.funding_mode === "MULTI") {
      if (formData.funding_sources.length === 0)
        issues.push("Add at least one Funding Source for multi-funding.");
      else {
        const fundingTotal = formData.funding_sources.reduce((s, f) => s + (parseFloat(f.amount) || 0), 0);
        if (Math.abs(fundingTotal - parseFloat(formData.estimated_value || 0)) >= 1)
          issues.push("Funding sources total (\u20B9" + fundingTotal.toLocaleString("en-IN") + ") must equal estimated value (\u20B9" + (parseFloat(formData.estimated_value) || 0).toLocaleString("en-IN") + ").");
      }
    }
    if (formData.procurement_channel === "NON_GEM" && !(formData.justification_note || "").trim())
      issues.push("Fill the Non-GeM Justification (Why not through GeM?) in the Document Requirements section.");
    if (formData.purpose === "OTHERS" && !(formData.purpose_detail || "").trim())
      issues.push("Specify the Purpose detail when 'Others' is selected.");
    if (formData.procurement_channel === "NON_GEM" && procurementInfo?.mode === "PAC_LPC" && !(formData.pac_justification || "").trim())
      issues.push("Fill the PAC Justification field (required for Non-GeM PAC/LPC purchases).");
    return issues;
  })();

  // Actions
  const actionsSection = !isViewMode
    ? React.createElement("div", { className: "space-y-3 pt-6 border-t" },
        // Inline validation banner
        validationIssues.length > 0 && React.createElement("div", {
          className: "p-4 rounded-xl border border-amber-300 bg-amber-50",
          "data-testid": "validation-summary"
        },
          React.createElement("p", { className: "text-sm font-semibold text-amber-900 mb-2" },
            "\u26A0\uFE0F Please complete the following before submitting (",
            validationIssues.length, " item",
            validationIssues.length > 1 ? "s" : "", "):"
          ),
          React.createElement("ul", { className: "list-disc ml-5 space-y-1" },
            ...validationIssues.map((msg, i) => React.createElement("li", { key: i, className: "text-sm text-amber-800" }, msg))
          )
        ),
        React.createElement("div", { className: "flex justify-end gap-4" },
          React.createElement(Button, { type: "button", variant: "outline", onClick: () => navigate("/requisitions") }, "Cancel"),
          React.createElement(Button, {
            type: "submit",
            className: "bg-slate-900 hover:bg-slate-800",
            disabled: submitting || validationIssues.length > 0,
            title: validationIssues.length > 0 ? validationIssues.join("\n") : "",
            "data-testid": "submit-pr-btn"
          }, submitting ? "Submitting..." : (validationIssues.length > 0 ? "Fix " + validationIssues.length + " issue" + (validationIssues.length > 1 ? "s" : "") + " to submit" : (isEditMode ? "Update Requisition" : "Submit Purchase Indent")))
        )
      )
    : null;

  return React.createElement("div", { className: "space-y-2 max-w-5xl", "data-testid": "requisition-form-page" },
    headerSection,
    approvalSection,
    React.createElement("form", { onSubmit: handleSubmit },
      indenterCard,
      procurementChannelCard,
      classificationCard,
      fundingCard,
      itemsCard,
      equipmentCard,
      tenderDetailsCard,
      committeeCard,
      datacentreCard,
      documentsCard,
      // Mode-aware document upload checklist (file attachments)
      formData.procurement_channel ? React.createElement(DocumentChecklist, {
        requisitionId: id,
        procurementChannel: formData.procurement_channel,
        procurementMode: procurementInfo?.mode,
        getAuthHeader: getAuthHeader,
        token: token,
        isViewMode: isViewMode
      }) : null,
      certificationsCard,
      actionsSection
    )
  );
}

export default PurchaseRequisitionForm;
